import type {
  ShopifyImage,
  ShopifyProduct,
  ShopifyProductMedia,
  ShopifyProductVariant,
} from "../api/ShopifyApi";

export interface UrlUtilParams {
  rootUrl: string;
  shopDomain: string;
  wishlistUrl: string;
  fileUrl: string;
  placeholderImageUrl?: string;
}

export class UrlUtil {
  rootUrl: string;
  shopDomain: string;
  wishlistUrl: string;
  fileUrl: string;
  placeholderImageUrl?: string;

  constructor(params: UrlUtilParams) {
    this.rootUrl = params.rootUrl;
    this.shopDomain = params.shopDomain;
    this.wishlistUrl = params.wishlistUrl;
    this.placeholderImageUrl = params.placeholderImageUrl;
    this.fileUrl = params.fileUrl;
  }

  getPublicWishlistUrl(wishlistId: string) {
    return `https://${this.shopDomain}${this.wishlistUrl}/${wishlistId}`;
  }

  getFileUrl(file: string = "") {
    return `https:${this.fileUrl.split("?")[0]}${file}`;
  }

  getImageUrl(media: Media, size?: MediaSize) {
    if (!media) {
      return this.placeholderImageUrl ?? "";
    }

    const image = this.getImage(media);

    if (!image?.src) {
      return this.placeholderImageUrl ?? "";
    }

    return this.appendSize(image.src, size);
  }

  getImage(media: Media): { src: string } | null {
    if ((media as ShopifyProductVariant).hasOwnProperty("sku")) {
      const variant = media as ShopifyProductVariant;
      return {
        src: variant.featured_image?.src ?? "",
      };
    } else if ((media as ShopifyProduct).variants) {
      const product = media as ShopifyProduct;
      return {
        src: product.featured_image ?? product.images[0],
      };
    } else if (
      (
        media as ShopifyProductMedia | ShopifyImage | ImageObject
      ).hasOwnProperty("src")
    ) {
      const image = media as ShopifyImage;
      return {
        src: image.src,
      };
    }
    return null;
  }

  appendSize(url: string, size?: MediaSize) {
    const newUrl = new URL(url.startsWith("//") ? `https://${url}` : url);

    if (size?.width) {
      newUrl.searchParams.set("width", size?.width.toString());
    }
    if (size?.height) {
      newUrl.searchParams.set("height", size?.height.toString());
    }

    return newUrl.href;
  }

  getVariantUrl(productUrl: string, variantId?: number) {
    if (!productUrl) {
      return;
    }
    if (!variantId) {
      return productUrl;
    }
    return productUrl + `?variant=${variantId}`;
  }

  handlelize(value: string) {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove accents
      .replace(/([^\w]+|\s+)/g, "-") // Replace space and other characters by hyphen
      .replace(/\-\-+/g, "-") // Replaces multiple hyphens by one hyphen
      .replace(/(^-+|-+$)/g, "") // Remove extra hyphens from beginning or end of the string
      .toLowerCase(); // To lowercase
  }
}

export type Media =
  | ShopifyProduct
  | ShopifyProductVariant
  | ShopifyProductMedia
  | ShopifyImage
  | ImageObject;

export interface ImageObject {
  src: string;
}
export interface MediaSize {
  width?: number;
  height?: number;
}
